import React, { useEffect } from 'react';
import { BsChevronDown, BsChevronExpand, BsChevronUp } from 'react-icons/bs';
import { GoTriangleDown, GoTriangleUp } from 'react-icons/go';
import { usePagination, useSortBy, useTable } from 'react-table';
import { Box, Flex, Icon, Table, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import Pagination from '../Pagination';

import './styles.scss';

const SortIcon = ({ column }) => {
  if (column.disableSortBy) return null;
  return column.isSorted
    ? (column.isSortedDesc
      ? <Icon as={BsChevronDown} ml={1} />
      : <Icon as={BsChevronUp} ml={1} />)
    : <Icon as={BsChevronExpand} ml={1} />;
};

function PaginatedTable({
  columns,
  data,
  pageInfo,
  handlePageChange,
  handleStateChange,
  hiddenColumns,
  maxHeight = '800px'
}) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    state: { sortBy }
  } = useTable({
    columns,
    data: data || [],
    manualSortBy: true,
    autoResetSortBy: false,
    rowKey: 'id',
    initialState: {
      hiddenColumns
    }
  }, useSortBy, usePagination);

  useEffect(() => {
    handleStateChange({ sortBy });
  }, [handleStateChange, sortBy]);

  const showPagination = pageInfo && pageInfo.lastPage > 1;

  return (
    <Box pb={showPagination ? '56px' : 0} position="relative">
      <Box className="PaginatedTable" maxHeight={maxHeight} overflowX="auto" overflowY="auto" position="relative">
        <Table variant="simple" {...getTableProps()} className="ReportContent">
          <Thead>
            { headerGroups.map(headerGroup => (
              <Tr {...headerGroup.getHeaderGroupProps()}>
                { headerGroup.headers.map(column => {
                  const headerProps = {
                    ...column.getSortByToggleProps(),
                    colSpan: column.colSpan || 2
                  };

                  return (
                    <Th
                      {...column.getHeaderProps(headerProps)}
                      bg="greyScale.100"
                      color="greyScale.900"
                      isNumeric={column.isNumeric}
                      textAlign="center"
                      textStyle="bodySm600"
                      textTransform="none"
                    >
                      <Flex alignItems="center" justifyContent={column.justifyContent || 'center'} whiteSpace="nowrap">
                        {column.render('Header')}
                        <SortIcon column={column} />
                      </Flex>
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          <Tbody {...getTableBodyProps()}>
            {rows.map(row => {
              prepareRow(row);

              return (
                <Tr {...row.getRowProps()} backgroundColor="white">
                  {row.cells.map(cell => {
                    const cellProps = {
                      colSpan: cell.column.colSpan || 2
                    };

                    return (
                      <Td {...cell.getCellProps(cellProps)} isNumeric={cell.column.isNumeric}>
                        {cell.render('Cell')}
                      </Td>
                    );
                  })}
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
      {
        showPagination && (
          <Pagination
            currentPage={pageInfo.currentPage}
            from={pageInfo.from}
            handlePageSelection={handlePageChange}
            pageCount={pageInfo.lastPage}
            perPage={pageInfo.perPage}
            style={{
              bottom: 0,
              left: 0,
              right: 0,
              position: 'absolute'
            }}
            to={pageInfo.to}
          />
        )
      }
    </Box>
  );
}

PaginatedTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  columns: PropTypes.arrayOf(PropTypes.object),
  pageInfo: PropTypes.shape({
    currentPage: PropTypes.number,
    from: PropTypes.number,
    to: PropTypes.number,
    perPage: PropTypes.number,
    lastPage: PropTypes.number
  }),
  handlePageChange: PropTypes.func,
  handleStateChange: PropTypes.func,
  hiddenColumns: PropTypes.arrayOf(PropTypes.string),
  maxHeight: PropTypes.string
};

PaginatedTable.defaultProps = {
  maxHeight: '800px',
  hiddenColumns: [],
  handlePageChange: () => {},
  handleStateChange: () => {}
};

export default PaginatedTable;
